import type { StateCreator } from 'zustand';
import {
  getAccountNavigationKey,
  getAccountSession,
} from '../../auth/accountSession';

export type ProfileGroupOrigin = {
  profileId: string;
  groupId: string;
  organizationId?: string;
  name: string;
};

export type GroupDetailsVisit = {
  peopleTab: 'children' | 'staff';
  scrollTop: number;
};

type NavigationState = {
  profileGroupOrigin: ProfileGroupOrigin | null;
  groupDetailsVisits: Record<string, GroupDetailsVisit>;
  classSearches: Record<string, string>;
};

export interface GroupNavigationState extends NavigationState {
  setProfileGroupOrigin: (origin: ProfileGroupOrigin | null) => void;
  saveGroupDetailsVisit: (path: string, visit: GroupDetailsVisit) => void;
  setClassSearch: (organizationId: string, search: string) => void;
}

const emptyState = (): NavigationState => ({
  profileGroupOrigin: null,
  groupDetailsVisits: {},
  classSearches: {},
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isOrigin = (value: unknown): value is ProfileGroupOrigin =>
  isRecord(value) &&
  typeof value.profileId === 'string' &&
  typeof value.groupId === 'string' &&
  typeof value.name === 'string' &&
  (value.organizationId === undefined ||
    typeof value.organizationId === 'string');

const isVisit = (value: unknown): value is GroupDetailsVisit =>
  isRecord(value) &&
  (value.peopleTab === 'children' || value.peopleTab === 'staff') &&
  typeof value.scrollTop === 'number' &&
  Number.isFinite(value.scrollTop) &&
  value.scrollTop >= 0;

function readNavigation(key: string | null): NavigationState {
  if (!key) return emptyState();
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    if (
      isRecord(value) &&
      (value.profileGroupOrigin === null ||
        isOrigin(value.profileGroupOrigin)) &&
      isRecord(value.groupDetailsVisits) &&
      isRecord(value.classSearches)
    ) {
      const groupDetailsVisits: Record<string, GroupDetailsVisit> = {};
      const classSearches: Record<string, string> = {};
      for (const [path, visit] of Object.entries(value.groupDetailsVisits)) {
        if (isVisit(visit)) groupDetailsVisits[path] = visit;
      }
      for (const [orgId, search] of Object.entries(value.classSearches)) {
        if (typeof search === 'string') classSearches[orgId] = search;
      }
      return {
        profileGroupOrigin: value.profileGroupOrigin,
        groupDetailsVisits,
        classSearches,
      };
    }
  } catch {
    // A blocked storage API or damaged entry must not prevent navigation.
  }
  return emptyState();
}

// Restore only after the server verifies the account. Session storage keeps this
// journey local to the tab; account changes and logout clear it.
export const createGroupNavigationSlice: StateCreator<GroupNavigationState> = (
  set,
  get
) => {
  const session = getAccountSession();
  const key = getAccountNavigationKey();
  const update = (partial: Partial<NavigationState>) => {
    if (getAccountSession() !== session) return;
    set(partial);
    if (!key) return;
    const { profileGroupOrigin, groupDetailsVisits, classSearches } = get();
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({
          profileGroupOrigin,
          groupDetailsVisits,
          classSearches,
        })
      );
    } catch {
      // In-memory navigation remains available without browser storage.
    }
  };
  return {
    ...readNavigation(key),
    setProfileGroupOrigin: (profileGroupOrigin) =>
      update({ profileGroupOrigin }),
    saveGroupDetailsVisit: (path, visit) =>
      update({
        groupDetailsVisits: { ...get().groupDetailsVisits, [path]: visit },
      }),
    setClassSearch: (organizationId, search) =>
      update({
        classSearches: { ...get().classSearches, [organizationId]: search },
      }),
  };
};
